import React, { useCallback, useEffect, useRef } from 'react';
import { adminAnalyticsAPI } from '../../../services/api';
import useSalesRange from './useSalesRange';
import useSalesQuery from './useSalesQuery';
import DateRangeControl from './DateRangeControl';
import KpiCards, { KPI_GRID, KPI_SPANS } from './KpiCards';
import CompareStrip from './CompareStrip';
import SalesChart, { CHART_HEIGHT } from './SalesChart';
import ShareDonut from './ShareDonut';
import BucketTable from './BucketTable';
import { Bone, EmptyState, ErrorState, InlineError, RefetchBar } from './SalesStates';
import { formatCurrency, formatRangeLabel } from './salesUtils';

// Skeleton is built from the same card shells, grid and chart height as the
// loaded layout (KPI row, compare strip, chart + donut, table) so nothing jumps.
const PanelSkeleton = () => (
  <div className="space-y-4" data-testid="video-sales-skeleton" aria-busy="true" aria-label="Loading sales">
    <div className={KPI_GRID}>
      {KPI_SPANS.map((span, i) => (
        <div key={i} className={`sales-card sales-kpi ${span}`}>
          <Bone className="h-3 w-20" />
          <Bone className="h-7 sm:h-8 w-24" />
          <Bone className="h-3 w-28 mt-auto" />
        </div>
      ))}
    </div>
    <div className="sales-card flex flex-col sm:flex-row gap-4 sm:gap-8">
      <div className="flex-1 space-y-3"><Bone className="h-4 w-2/3" /><Bone className="h-1.5 w-full" /></div>
      <div className="flex-1 space-y-3"><Bone className="h-4 w-2/3" /><Bone className="h-1.5 w-full" /></div>
    </div>
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="sales-card lg:col-span-2">
        <Bone className="h-4 w-40 mb-4" />
        <Bone className={`${CHART_HEIGHT} w-full`} />
      </div>
      <div className="sales-card flex flex-col items-center">
        <Bone className="h-4 w-36 mb-4 self-start" />
        <Bone className="h-40 w-40 rounded-full my-auto" />
      </div>
    </div>
    <div className="sales-card space-y-3">
      <Bone className="h-4 w-24" />
      {[0, 1, 2, 3].map((i) => <Bone key={i} className="h-6 w-full" />)}
    </div>
  </div>
);

const VideoThumb = ({ url }) => (url ? (
  <img src={url} alt="" className="sales-thumb w-20 sm:w-32" />
) : (
  <div className="sales-thumb w-20 sm:w-32 flex items-center justify-center text-gray-700" aria-hidden="true">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
  </div>
));

const PanelHeader = ({ video, rangeLabel, onClose }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
    <button type="button" onClick={onClose} className="sales-btn sales-btn-ghost self-start sm:self-center shrink-0 -ml-2 sm:ml-0" data-testid="video-sales-close" aria-label="Close sales panel">
      <span aria-hidden="true">&larr;</span> Back
    </button>
    {video ? (
      <div className="flex items-center gap-3 sm:gap-4 min-w-0 sales-enter">
        <VideoThumb url={video.thumbnail_url} />
        <div className="min-w-0">
          <p className="sales-label mb-1">Sales analytics</p>
          <p className="sales-title truncate">{video.title}</p>
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-1.5 text-xs text-gray-400">
            <span className="sales-num text-gray-200 font-medium">{video.free ? 'Free' : formatCurrency(video.price_cents, video.currency)}</span>
            <span className="sales-pill">
              <span className={`w-1.5 h-1.5 rounded-full ${video.status === 'public' ? 'bg-army-light' : 'bg-gray-500'}`} aria-hidden="true" />
              {video.status}
            </span>
            {rangeLabel && <span className="sales-num">{rangeLabel}</span>}
          </div>
        </div>
      </div>
    ) : (
      <div className="flex items-center gap-3 sm:gap-4">
        <Bone className="w-20 sm:w-32 aspect-video" />
        <div className="space-y-2"><Bone className="w-24 h-3" /><Bone className="w-48 h-6" /><Bone className="w-32 h-3" /></div>
      </div>
    )}
  </div>
);

// Sales analytics for ONE video: KPIs, share, trend, per-bucket table + CSV.
const VideoSalesPanel = ({ videoId, onClose }) => {
  const { range, params, setRange } = useSalesRange();
  const fetcher = useCallback(
    ({ videoId: id, ...rest }, opts) => adminAnalyticsAPI.videoSales(id, rest, opts),
    []
  );
  const { data, error, loading, retry } = useSalesQuery(fetcher, { videoId, ...params });

  // Move focus into the panel when it opens (the button that opened it unmounts).
  // The heading is rendered in every state so focus is not lost when data arrives.
  const headingRef = useRef(null);
  useEffect(() => { headingRef.current?.focus(); }, []);

  const meta = data?.meta;
  const currency = meta?.currency || 'usd';
  const granularity = meta?.granularity || 'day';
  const rangeLabel = meta ? formatRangeLabel(meta.from, meta.to) : formatRangeLabel(params.from, params.to);

  let body;
  if (!data && error) {
    body = <ErrorState message="Could not load sales for this video." onRetry={retry} testId="video-sales-error" />;
  } else if (!data) {
    body = <PanelSkeleton />;
  } else {
    const hasSales = data.range.sales_count > 0;
    body = (
      <div className="space-y-4 sales-enter">
        {error && <InlineError onRetry={retry} />}
        <KpiCards range={data.range} currency={currency} rangeLabel={rangeLabel} />
        <CompareStrip range={data.range} allTime={data.all_time} />
        {hasSales ? (
          <>
            <div className="grid lg:grid-cols-3 gap-4 items-stretch">
              <div className="lg:col-span-2 min-w-0"><SalesChart series={data.series} currency={currency} granularity={granularity} /></div>
              <ShareDonut range={data.range} currency={currency} />
            </div>
            <BucketTable
              series={data.series}
              currency={currency}
              granularity={granularity}
              filename={`video-${videoId}-sales-${meta.from}-to-${meta.to}.csv`}
            />
          </>
        ) : (
          <EmptyState title="No sales in this range" testId="video-sales-empty">
            This video has no sales between these dates. Try a wider range or All time.
          </EmptyState>
        )}
      </div>
    );
  }

  return (
    <section className="relative space-y-4 sm:space-y-5 min-w-0" data-testid="video-sales-panel" aria-label="Video sales">
      <h2 ref={headingRef} tabIndex={-1} className="sr-only" data-testid="video-sales-heading">
        {data?.video ? `Sales analytics: ${data.video.title}` : 'Sales analytics'}
      </h2>
      {loading && data && <RefetchBar />}
      <PanelHeader video={data?.video} rangeLabel={rangeLabel} onClose={onClose} />
      <DateRangeControl
        range={range}
        resolved={{ from: params.from, to: params.to }}
        onChange={setRange}
        currencies={meta?.currencies}
        currency={currency}
        onCurrencyChange={(c) => setRange({ currency: c })}
      />
      {body}
    </section>
  );
};

export default VideoSalesPanel;
